import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import toast from 'react-hot-toast'
import { InlineBanner } from '@/components/commons/inline-banner'
import { InfoCircleIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { formatDate } from '@/dayjs'
import { AssignCtaControl } from '@/features/marquee/components/assign-cta-control'
import type { ComboboxOption } from '@/features/marquee/components/combobox-field'
import { CtaBadge } from '@/features/marquee/components/cta-badge'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import { attendeesQueryOptions } from '@/features/marquee/query-options'
import {
	attendeeCtaErrorKey,
	type CtaCaptureBlock,
	ctaCaptureBlockToLabelKey,
	ctaCaptureBlockToTitleKey,
	invalidStateMessage,
	isCtaSetTailored
} from '@/features/marquee/utils'
import { errorHandler } from '@/helpers/utils'
import {
	type MarqueeAttendee,
	type MarqueeAttendeeCta,
	type MarqueeEventState,
	setAttendeeCtas
} from '@/sdk'
import { useTranslation } from '@/translations/i18n'

interface CtaPanelProps {
	/** The roster row: the real CTA state (`ctas`, `ctas_customized_at`). */
	attendee: MarqueeAttendee
	/** Owning event — the roster cache the optimistic write lands in. */
	eventId?: string
	/**
	 * The event's lifecycle state, used only to word a wrong-state refusal: the
	 * write answers the shared 409 `invalid-state`, which names no action.
	 */
	eventState?: MarqueeEventState
	/** Why capture is unavailable, or `null` when the operator can edit. */
	block: CtaCaptureBlock | null
	/** The event's CTA library, the only codes the backend accepts. */
	ctaOptions: ComboboxOption[]
	/** The event's IANA zone: the capture instant is read in the market's clock. */
	timeZone?: string
}

export function AttendeeDetailCtaPanel({
	attendee,
	eventId,
	eventState,
	block,
	ctaOptions,
	timeZone
}: CtaPanelProps) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const attendeesKey = attendeesQueryOptions(eventId).queryKey

	// The selection is server state, read straight off the roster cache — the
	// optimistic write below is what makes an edit feel instant, so there is no
	// second copy of it in component state to drift. Read carries `{ code,
	// label }` per CTA so a row renders without a second lookup; the write takes
	// codes only.
	const pitched = useMemo(() => attendee.ctas ?? [], [attendee.ctas])
	const pitchedSet = useMemo(
		() => new Set(pitched.map((cta) => cta.code)),
		[pitched]
	)

	/**
	 * Full replacement of this attendee's set (PUT /attendees/{id}/ctas). The
	 * endpoint is idempotent, so resending the same body is harmless and a lost
	 * response cannot double-apply: the write is confirmed optimistically and
	 * settled in the background.
	 */
	const saveCtas = useMutation({
		mutationFn: async (next: MarqueeAttendeeCta[]) => {
			const response = await setAttendeeCtas({
				path: { attendeeId: attendee.id },
				body: { ctas: next.map((cta) => cta.code) }
			})
			return response.data
		},
		onMutate: async (next) => {
			await queryClient.cancelQueries({ queryKey: attendeesKey })
			const previous = queryClient.getQueryData(attendeesKey)
			queryClient.setQueryData(
				attendeesKey,
				(current) =>
					current && {
						...current,
						rows: current.rows?.map((row) =>
							row.id === attendee.id
								? {
										...row,
										ctas: next,
										// Any write is a tailoring, clearing included:
										// an empty set is still 1:1, never a revert to
										// the event blanket.
										ctas_customized_at:
											new Date().toISOString()
									}
								: row
						)
					}
			)
			return { previous }
		},
		onError: (error, _next, context) => {
			queryClient.setQueryData(attendeesKey, context?.previous)
			// `invalid-state` first: it is the code seven writes share, so the
			// shared mapping words it for capture specifically. The other three
			// codes this endpoint documents are its own and keep their sentence.
			const refusal = invalidStateMessage({
				error,
				action: 'capture_ctas',
				eventState
			})
			if (refusal) {
				toast.error(
					t(refusal.key, {
						state: refusal.stateKey ? t(refusal.stateKey) : ''
					})
				)
				return
			}
			const messageKey = attendeeCtaErrorKey(error)
			if (messageKey) {
				toast.error(t(messageKey))
				return
			}
			errorHandler(error)
		},
		// Settle against the server's own copy, whichever way it went, on the
		// whole event prefix rather than on the roster alone: the export
		// preview is a sibling key and carries this attendee's CTA count, so
		// invalidating only the roster left that table showing the count from
		// before the edit until its 10-minute staleness ran out.
		onSettled: () =>
			queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.events.detail(eventId ?? '')
			})
	})

	// Resolve a CTA code to a label from the event's CTA library. `label` is
	// optional on MarqueeAttendeeCta, so this covers a row that arrives with a
	// bare code; a code the library no longer carries renders as the code
	// itself. Deliberately no mock fallback: the synthesised past-CTA titles
	// used to feed this map, which let an invented title caption a real saved
	// CTA.
	const labelForCode = useMemo(() => {
		const map = new Map(
			ctaOptions.map((option) => [option.value, option.label])
		)
		return (code: string) => map.get(code) ?? code
	}, [ctaOptions])

	const pitchedOptions = useMemo<ComboboxOption[]>(
		() =>
			pitched.map((cta) => ({
				value: cta.code,
				label: cta.label ?? labelForCode(cta.code)
			})),
		[pitched, labelForCode]
	)

	const canCapture = block === null
	const isTailored = isCtaSetTailored(attendee)

	return (
		<div className='flex min-w-0 flex-1 flex-col gap-8 bg-neutral-white l:px-10 px-4 pt-6 pb-10'>
			{block && (
				<InlineBanner
					description={t(ctaCaptureBlockToLabelKey[block], {
						name: attendee.name || '-'
					})}
					icon={
						<InfoCircleIcon className='size-6 text-neutral-500' />
					}
					title={t(ctaCaptureBlockToTitleKey[block])}
				/>
			)}

			{/* ── CTAs captured for this attendee ───────────────────────── */}
			<section className='flex flex-col gap-4'>
				<SectionHeader
					subtitle={
						isTailored
							? t('marquee:attendee_detail_cta_tailored', {
									date: formatDate(
										attendee.ctas_customized_at,
										'lll',
										timeZone
									)
								})
							: t('marquee:attendee_detail_cta_from_blanket')
					}
					title={t('marquee:attendee_detail_pitched_title')}
				/>

				{canCapture && (
					<div className='flex flex-col gap-3'>
						<label
							className='flex items-center gap-2 font-medium text-neutral-900 text-sm'
							htmlFor='assign-ctas'
						>
							{t('marquee:attendee_detail_assign_ctas')}
							{saveCtas.isPending && <Spinner size={14} />}
						</label>
						<AssignCtaControl
							emptyLabel={t(
								'marquee:attendee_detail_assign_empty'
							)}
							id='assign-ctas'
							onDeselect={(option) =>
								saveCtas.mutate(
									pitched.filter(
										(candidate) =>
											candidate.code !== option.value
									)
								)
							}
							onSelect={(option) => {
								if (pitchedSet.has(option.value)) {
									return
								}
								// Newest first, so an addition appears at the
								// front of the pill row instead of getting
								// lost past the scroll.
								saveCtas.mutate([
									{
										code: option.value,
										label: option.label
									},
									...pitched
								])
							}}
							options={ctaOptions}
							pitched={pitchedOptions}
							placeholder={t(
								'marquee:attendee_detail_assign_placeholder'
							)}
							removeLabel={(cta) =>
								t('marquee:attendee_detail_remove_cta', {
									cta
								})
							}
							searchClearLabel={t(
								'marquee:attendee_detail_assign_search_clear'
							)}
							searchPlaceholder={t(
								'marquee:attendee_detail_assign_search'
							)}
						/>
					</div>
				)}

				{pitchedOptions.length > 0 ? (
					<div className='flex flex-wrap gap-2'>
						{pitchedOptions.map((option) => (
							<CtaBadge
								key={option.value}
								label={option.label}
								onRemove={
									canCapture
										? () =>
												saveCtas.mutate(
													pitched.filter(
														(candidate) =>
															candidate.code !==
															option.value
													)
												)
										: undefined
								}
								removeLabel={t(
									'marquee:attendee_detail_remove_cta',
									{ cta: option.label }
								)}
							/>
						))}
					</div>
				) : (
					<p className='text-neutral-500 text-sm'>
						{t('marquee:attendee_detail_pitched_empty')}
					</p>
				)}
			</section>

			{/* HIDDEN: Past CTAs. Deferred to OPSSTD-5151, blocked by
			    OPSSTD-5149 (the Summit resolution snapshot) — its columns ride
			    along on that deliverable and may not be produced at all. The
			    decision was build it whole or not at all, so no partial version
			    is coming and this stays hidden rather than degraded.

			    It showed synthesised adopted / no-action outcomes against
			    invented contexts like "Q2 Prohost Summit · May 2026" — pitch
			    history an MM could act on. The `attendee_detail_past_subtitle`
			    caption ("Historical from previous events / Salesforce") goes
			    with it: it claims a provenance we do not have. Its i18n keys are
			    kept for the restore.

			    There is no view-model any more — the component reads the roster
			    row directly — so restoring means a `past_ctas` array of
			    `{ code, title, context, outcome: 'adopted' | 'no_action' }` on
			    `MarqueeAttendee`, read as `attendee.past_ctas`.

			<section className='flex flex-col gap-4'>
				<SectionHeader
					subtitle={t('marquee:attendee_detail_past_subtitle')}
					title={t('marquee:attendee_detail_past_title', {
						name: attendee.name || '-'
					})}
				/>
				<div className='flex flex-col gap-4'>
					{attendee.past_ctas.map((cta) => (
						<PastCtaCard cta={cta} key={cta.code} />
					))}
				</div>
			</section>
			*/}
		</div>
	)
}

function SectionHeader({
	title,
	subtitle
}: {
	title: string
	subtitle: string
}) {
	return (
		<div className='flex flex-col gap-1'>
			<h2 className='font-medium text-neutral-900 text-xl'>{title}</h2>
			<p className='text-neutral-500 text-sm'>{subtitle}</p>
		</div>
	)
}

/*
 * HIDDEN with the Past CTAs section above (OPSSTD-5151). Restore both together;
 * the `PastCta` type import at the top of the file comes back with it.
 *
function PastCtaCard({ cta }: { cta: PastCta }) {
	const { t } = useTranslation()
	return (
		<div className='flex items-center justify-between gap-4 rounded-xl bg-grey-100 p-6'>
			<div className='flex min-w-0 flex-col gap-0.5'>
				<p className='font-medium text-neutral-900 text-sm'>
					{cta.title}
				</p>
				<p className='text-neutral-500 text-xs'>
					{cta.context}
					{' · '}
					{t(
						cta.outcome === 'adopted'
							? 'marquee:attendee_detail_outcome_adopted_hint'
							: 'marquee:attendee_detail_outcome_no_action_hint'
					)}
				</p>
			</div>
			{cta.outcome === 'adopted' ? (
				<span className='inline-flex shrink-0 items-center rounded-full bg-success-600/10 px-3 py-1.5 font-medium text-success-700 text-xs'>
					{t('marquee:attendee_detail_outcome_adopted')}
				</span>
			) : (
				<span className='inline-flex shrink-0 items-center gap-1.5 rounded-full bg-grey-200 px-3 py-1.5 font-medium text-grey-1000 text-xs'>
					<span className='size-2 rounded-full bg-grey-500' />
					{t('marquee:attendee_detail_outcome_no_action')}
				</span>
			)}
		</div>
	)
}
*/
